/**
 * UTM Parameter Preservation
 * Captures UTM parameters from URL and appends them to all internal links
 */
(function() {
  'use strict';

  // UTM parameter keys to preserve
  const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'utm_publisher_ID' // Also preserve publisher ID if present
  ];

  /**
   * Get all URL parameters from the current URL
   */
  function getUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {};
    
    UTM_PARAMS.forEach(param => {
      const value = params.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });

    return utmParams;
  }

  /**
   * Get stored UTM parameters from sessionStorage
   */
  function getStoredUtmParams() {
    try {
      const stored = sessionStorage.getItem('utm_params');
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Store UTM parameters in sessionStorage
   */
  function storeUtmParams(params) {
    try {
      if (Object.keys(params).length > 0) {
        sessionStorage.setItem('utm_params', JSON.stringify(params));
      }
    } catch (e) {
      console.warn('Could not store UTM parameters:', e);
    }
  }

  /**
   * Check if URL is internal (same domain)
   */
  function isInternalUrl(url) {
    try {
      const linkUrl = new URL(url, window.location.origin);
      return linkUrl.hostname === window.location.hostname || 
             linkUrl.hostname === window.location.hostname.replace('www.', '') ||
             window.location.hostname.replace('www.', '') === linkUrl.hostname.replace('www.', '');
    } catch (e) {
      // If URL parsing fails, check if it's a relative URL
      return url.startsWith('/') || url.startsWith('#') || !url.includes('://');
    }
  }

  /**
   * Append UTM parameters to a URL
   */
  function appendUtmParams(url, utmParams) {
    if (Object.keys(utmParams).length === 0) {
      return url;
    }

    try {
      // Handle relative URLs
      const baseUrl = url.includes('://') ? url : window.location.origin + (url.startsWith('/') ? url : '/' + url);
      const urlObj = new URL(baseUrl);
      
      // Append UTM parameters
      Object.keys(utmParams).forEach(key => {
        // Don't override existing parameters
        if (!urlObj.searchParams.has(key)) {
          urlObj.searchParams.set(key, utmParams[key]);
        }
      });

      // Return relative URL if original was relative
      if (!url.includes('://')) {
        return urlObj.pathname + urlObj.search + urlObj.hash;
      }

      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, append as query string
      const separator = url.includes('?') ? '&' : '?';
      const queryString = Object.keys(utmParams)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(utmParams[key])}`)
        .join('&');
      return url + separator + queryString;
    }
  }

  /**
   * Initialize UTM parameter preservation
   */
  function init() {
    // Get UTM parameters from current URL
    const urlParams = getUrlParams();
    
    // Get stored parameters
    const storedParams = getStoredUtmParams();
    
    // Merge: URL params take precedence, then stored params
    const activeParams = { ...storedParams, ...urlParams };
    
    // Store/update the active parameters
    if (Object.keys(activeParams).length > 0) {
      storeUtmParams(activeParams);
    }

    // Function to update all links on the page
    function updateLinks() {
      const links = document.querySelectorAll('a[href]');
      const params = Object.keys(activeParams).length > 0 ? activeParams : getStoredUtmParams();

      if (Object.keys(params).length === 0) {
        return;
      }

      links.forEach(link => {
        const href = link.getAttribute('href');
        
        if (!href || href === '#' || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          return;
        }

        // Only process internal links
        if (isInternalUrl(href)) {
          const updatedUrl = appendUtmParams(href, params);
          
          // Only update if URL actually changed
          if (updatedUrl !== href) {
            link.setAttribute('href', updatedUrl);
          }
        }
      });
    }

    // Update links on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateLinks);
    } else {
      updateLinks();
    }

    // Also handle dynamically added content (like AJAX-loaded content)
    const observer = new MutationObserver(function(mutations) {
      let shouldUpdate = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length > 0) {
          // Check if any added nodes contain links
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              if (node.tagName === 'A' || node.querySelectorAll('a[href]').length > 0) {
                shouldUpdate = true;
              }
            }
          });
        }
      });

      if (shouldUpdate) {
        // Use a small delay to batch updates
        setTimeout(updateLinks, 100);
      }
    });

    // Observe changes to the document body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Handle form submissions to preserve UTM params in form redirects
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.tagName === 'FORM' && form.action) {
        const params = Object.keys(activeParams).length > 0 ? activeParams : getStoredUtmParams();
        
        if (Object.keys(params).length > 0 && isInternalUrl(form.action)) {
          const updatedAction = appendUtmParams(form.action, params);
          if (updatedAction !== form.action) {
            form.action = updatedAction;
          }
        }
      }
    });
  }

  // Initialize when script loads
  init();
})();

